import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import logoSrc from '@/assets/logo.svg'

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, googleLogin, isLoading } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Persist product_url from query params to localStorage
  useEffect(() => {
    const productUrl = searchParams.get('product_url')
    if (productUrl) {
      localStorage.setItem('pending_product_url', productUrl)
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null) // Clear previous errors

    try {
      await login(email, password)
      navigate('/')
    } catch (error) {
      // Extract error message from API response
      let errorMessage = 'Incorrect email or password. Please try again.'

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: { message?: string }; detail?: string } } }
        // Try standard error format first (error.error.message)
        if (axiosError.response?.data?.error?.message) {
          errorMessage = axiosError.response.data.error.message
        }
        // Fallback to detail field (legacy format)
        else if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      // Display error in the login form instead of toast
      setError(errorMessage)
    }
  }

  const handleGoogleSuccess = async (response: { credential?: string }) => {
    if (!response.credential) {
      toast({
        title: 'Google login failed',
        description: 'No credential received from Google.',
        variant: 'destructive',
      })
      return
    }

    try {
      await googleLogin(response.credential)
      navigate('/')
    } catch {
      toast({
        title: 'Google login failed',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img src={logoSrc} alt="LinkToCreative" className="h-[7rem] object-contain" />
          </div>
          <h1 className="text-3xl font-bold font-brand tracking-tight text-gray-900">LinkToCreative</h1>
          <p className="mt-2 text-gray-600">Sign in to your account</p>
        </div>

        <div className="mt-8 bg-white p-8 rounded-xl border border-gray-200/60 shadow-sm space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-red-400 mr-2 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="Enter your password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                toast({
                  title: 'Google login failed',
                  description: 'Please try again.',
                  variant: 'destructive',
                })
              }}
              size="large"
              width="100%"
              text="signin_with"
            />
          </div>

          <p className="text-center text-sm text-gray-600">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
